import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { adminFetch } from '../lib/adminFetch';
import { API_BASE } from '../lib/apiBase';
import {
  LayoutDashboard,
  Calendar,
  Mail,
  Users,
  FileText,
  Activity,
  LogOut,
  Clock,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

// Tiny chime (~300ms two-tone beep) using Web Audio — no asset file needed.
function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const playTone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + dur);
    };
    playTone(880, now, 0.18);   // A5
    playTone(1175, now + 0.16, 0.22); // D6
    setTimeout(() => ctx.close().catch(() => {}), 700);
  } catch {
    /* silent */
  }
}

export const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout, msRemaining, refreshSession } = useAdminAuth();
  const [newRequestCount, setNewRequestCount] = useState(0);
  const prevCountRef = useRef(null);
  const userInteractedRef = useRef(false);

  // After first user interaction, allow audio (browser autoplay policy)
  useEffect(() => {
    const mark = () => {
      userInteractedRef.current = true;
    };
    window.addEventListener('click', mark, { once: true });
    window.addEventListener('keydown', mark, { once: true });
    return () => {
      window.removeEventListener('click', mark);
      window.removeEventListener('keydown', mark);
    };
  }, []);

  const BACKEND_URL = API_BASE;

  const fetchNewRequestCount = useCallback(async () => {
    if (!admin) return;
    try {
      const r = await adminFetch(`${BACKEND_URL}/api/admin/stats`, { credentials: 'include' });
      if (r.ok) {
        const s = await r.json();
        const next = s.new_requests ?? s.pending_appointments ?? 0;
        // Chime if count went UP (not on initial load)
        if (prevCountRef.current !== null && next > prevCountRef.current && userInteractedRef.current) {
          playChime();
          toast.info(`New appointment request received (${next} new)`, {
            duration: 6000,
            action: {
              label: 'View',
              onClick: () => navigate('/admin/appointments?status=new_request'),
            },
          });
        }
        prevCountRef.current = next;
        setNewRequestCount(next);
      }
    } catch {
      /* silent */
    }
  }, [admin, BACKEND_URL, navigate]);

  // Poll every 30s while admin is signed in
  useEffect(() => {
    if (!admin) {
      setNewRequestCount(0);
      prevCountRef.current = null;
      return undefined;
    }
    fetchNewRequestCount();
    const id = setInterval(fetchNewRequestCount, 30000);
    return () => clearInterval(id);
  }, [admin, fetchNewRequestCount]);

  // Refetch immediately when route changes (so closing a request decrements quickly)
  useEffect(() => {
    if (admin) fetchNewRequestCount();
  }, [location.pathname, admin, fetchNewRequestCount]);

  const navigation = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Appointments', path: '/admin/appointments', icon: Calendar, badge: newRequestCount },
    { name: 'Messages', path: '/admin/messages', icon: Mail },
    { name: 'Patients', path: '/admin/patients', icon: Users },
    { name: 'Medical Records', path: '/admin/medical-records', icon: FileText },
    { name: 'Vitals', path: '/admin/vitals', icon: Activity },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/admin/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  // Session banner: only show when < 5 min remaining
  const showBanner = admin && msRemaining > 0 && msRemaining < 5 * 60 * 1000;
  const minutesLeft = Math.max(1, Math.ceil(msRemaining / 60000));

  const handleExtend = async () => {
    const ok = await refreshSession();
    if (ok) toast.success('Session extended');
    else toast.error('Could not extend session — please log in again');
  };

  return (
    <div className="min-h-screen bg-gray-50 font-[Montserrat]">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-[#1C5C62] text-white">
        <div className="p-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_care-staging/artifacts/bh07xo4f_hw_logo_white.png" 
            alt="Healthwrx" 
            className="h-10 w-auto mb-2"
          />
          <p className="text-sm text-gray-300">Admin Portal</p>
        </div>

        <nav className="px-3 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const badge = item.badge && item.badge > 0 ? item.badge : null;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between space-x-3 px-3 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-[#14D1A2] text-white'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="flex items-center space-x-3">
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </span>
                {badge && (
                  <span
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center"
                    data-testid="new-requests-badge"
                  >
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="mb-4">
            <p className="text-sm text-gray-300">Logged in as:</p>
            <p className="font-medium truncate">{admin?.email}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-white/20 text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64">
        {showBanner && (
          <div
            className="sticky top-0 z-30 bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center justify-between gap-3 font-[Montserrat]"
            data-testid="session-expiry-banner"
          >
            <div className="flex items-center gap-2 text-amber-900 text-sm">
              <Clock className="w-4 h-4" />
              <span>
                Session expiring in <strong data-testid="session-minutes-left">{minutesLeft} min</strong>
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleExtend}
              className="h-8 border-amber-300 text-amber-900 hover:bg-amber-100"
              data-testid="session-extend-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Extend session
            </Button>
          </div>
        )}
        <div className="p-8">{children}</div>
      </div>
    </div>
  );
};
